<script setup lang="ts">
    import { reactive, watch, computed } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import MonthPicker from './MonthPicker.vue';
    import {
        EditableOrder,
        EditOrderRow,
        ClientCompanySearchResult,
        saveOrder,
        findClientCompany,
        newOrder,
    } from './Billing.access';

    const props = defineProps<{
        show: boolean;
        order: EditableOrder | null;
        bookkeepingAccounts: string[];
    }>();

    const emit = defineEmits<{
        (e: 'close'): void;
        (e: 'saved'): void;
    }>();

    const CURRENCIES = ['SEK', 'USD', 'DKK', 'NOK', 'EUR'];
    const LANGUAGES = [
        { value: 'sv', label: 'Swedish' },
        { value: 'en', label: 'English' },
        { value: 'fi', label: 'Finnish' },
        { value: 'Fr', label: 'French' },
    ];
    const ORDER_TYPES = ['Subscription', 'SubscriptionRenewal', 'SubscriptionUpgrade', 'PrePay', 'GiveAway', 'Service', 'Manual', 'Credit'];
    const AVAILABILITIES = [
        { value: 'ReadyToSend', label: 'Ready to send' },
        { value: 'Waiting', label: 'Waiting' },
    ];

    interface EditorState {
        form: EditableOrder;
        saving: boolean;
        companySearch: string;
        companyResults: ClientCompanySearchResult[];
        companySearchLoading: boolean;
    }

    const state = reactive<EditorState>({
        form: newOrder(),
        saving: false,
        companySearch: '',
        companyResults: [],
        companySearchLoading: false,
    });

    watch(
        () => props.order,
        (order) => {
            state.form = order ? { ...order, OrderRows: order.OrderRows.map((r) => ({ ...r })) } : newOrder();
            state.companySearch = order?.CompanyName ?? '';
        },
        { immediate: true },
    );

    const canSave = computed(() => {
        const s = state.form.StatusString;
        return !!state.form.ServiceSubscriptionId && (!s || s === 'ReadyToSend' || s === 'Waiting');
    });

    const isCreditOrder = computed(() => state.form.OrderType === 'Credit');

    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    function onCompanySearchInput(): void {
        if (searchTimeout) clearTimeout(searchTimeout);
        const val = state.companySearch;
        if (!val || val.length < 2) {
            state.companyResults = [];
            return;
        }
        searchTimeout = setTimeout(async () => {
            state.companySearchLoading = true;
            try {
                state.companyResults = await findClientCompany(val);
            } finally {
                state.companySearchLoading = false;
            }
        }, 500);
    }

    function selectCompany(company: ClientCompanySearchResult): void {
        state.form.ServiceSubscriptionId = company.ServiceSubscriptionId;
        state.form.CompanyName = company.BillingName || company.CompanyName;
        state.form.Attention = company.Attention ?? '';
        state.form.AddVat = company.PaysVat;
        state.form.RecipientName = company.BillingName || company.CompanyName;
        state.form.RecipientAddress = [company.BillingAddress1, company.BillingAddress2, company.BillingZipCode, company.BillingCity, company.BillingCountry]
            .filter(Boolean)
            .join('\n') || company.BillingAddress;
        state.form.RecipientEmail = company.BillingEmail ?? '';
        if (company.DefaultCurrencyCode) state.form.CurrencyCode = company.DefaultCurrencyCode;
        state.companySearch = state.form.CompanyName;
        state.companyResults = [];
    }

    function addRow(): void {
        const last = state.form.OrderRows[state.form.OrderRows.length - 1];
        state.form.OrderRows.push({
            RowNumber: null,
            Specification: last?.Specification ?? '',
            Amount: last?.Amount ?? 0,
            Vat: 0,
            BookkeepingAccount: last?.BookkeepingAccount ?? (props.bookkeepingAccounts[0] ?? ''),
            RefersToMonth: last?.RefersToMonth ?? null,
        });
    }

    function removeRow(index: number): void {
        state.form.OrderRows.splice(index, 1);
    }

    function moveRowUp(index: number): void {
        if (index === 0) return;
        const rows = state.form.OrderRows;
        [rows[index - 1], rows[index]] = [rows[index], rows[index - 1]];
    }

    function moveRowDown(index: number): void {
        const rows = state.form.OrderRows;
        if (index >= rows.length - 1) return;
        [rows[index], rows[index + 1]] = [rows[index + 1], rows[index]];
    }

    function getRevertInvoicedUntilDate(): string {
        const dates = state.form.OrderRows.map((r) => r.RefersToMonth).filter(Boolean) as string[];
        if (!dates.length) return '[No date found]';
        const earliest = dates.sort()[0];
        const d = new Date(earliest);
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().slice(0, 7);
    }

    function applyRevertDate(): void {
        state.form.InvoicedUntil = getRevertInvoicedUntilDate();
    }

    async function onSave(): Promise<void> {
        state.saving = true;
        try {
            await saveOrder(state.form);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Order saved' });
            emit('saved');
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to save order' });
        } finally {
            state.saving = false;
        }
    }
</script>

<template>
    <Modal :show="props.show" title="Order" size="xl" :scroll-body="true" @close="emit('close')">
        <template #content>
            <form @submit.prevent="onSave">
                <!-- Company search -->
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Find recipient</label>
                            <div style="position: relative">
                                <input
                                    type="text"
                                    class="form-control"
                                    placeholder="Search company"
                                    v-model="state.companySearch"
                                    @input="onCompanySearchInput" />
                                <ul
                                    v-if="state.companyResults.length > 0"
                                    class="dropdown-menu"
                                    style="display: block; width: 100%; max-height: 200px; overflow-y: auto">
                                    <li v-for="c in state.companyResults" :key="c.ServiceSubscriptionId">
                                        <a href="#" @click.prevent="selectCompany(c)">
                                            {{ c.BillingName || c.CompanyName }}
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6" v-if="state.form.ServiceSubscriptionId">
                        <div class="form-group">
                            <label>Selected company</label>
                            <p class="form-control-static">
                                <strong>{{ state.form.CompanyName }}</strong><br />
                                <small class="text-muted">{{ state.form.ServiceSubscriptionId }}</small>
                            </p>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-3">
                        <div class="form-group">
                            <label>Order type</label>
                            <select class="form-control" v-model="state.form.OrderType">
                                <option v-for="t in ORDER_TYPES" :key="t" :value="t">{{ t }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="form-group">
                            <label>Availability</label>
                            <select class="form-control" v-model="state.form.Availability">
                                <option v-for="a in AVAILABILITIES" :key="a.value" :value="a.value">{{ a.label }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="form-group">
                            <label>Language</label>
                            <select class="form-control" v-model="state.form.Language">
                                <option v-for="l in LANGUAGES" :key="l.value" :value="l.value">{{ l.label }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="form-group">
                            <label>Payment terms (days)</label>
                            <input type="number" class="form-control" min="10" max="120" v-model.number="state.form.PaymentTerms" />
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Recipient name</label>
                            <input type="text" class="form-control" v-model="state.form.RecipientName" />
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Your ref / attention</label>
                            <input type="text" class="form-control" v-model="state.form.Attention" />
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Recipient address</label>
                            <textarea class="form-control" rows="3" v-model="state.form.RecipientAddress"></textarea>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Recipient email</label>
                            <input type="email" class="form-control" v-model="state.form.RecipientEmail" />
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Specification</label>
                            <input type="text" class="form-control" v-model="state.form.Specification" />
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="form-group">
                            <label>Currency</label>
                            <select class="form-control" v-model="state.form.CurrencyCode">
                                <option v-for="c in CURRENCIES" :key="c" :value="c">{{ c }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="form-group">
                            <label>&nbsp;</label>
                            <div class="checkbox">
                                <label>
                                    <input type="checkbox" v-model="state.form.AddVat" />
                                    Add VAT to invoice
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Credit invoice section -->
                <div v-if="isCreditOrder" class="row">
                    <div class="col-md-4">
                        <div class="form-group">
                            <label>Invoiced until</label>
                            <MonthPicker v-model="state.form.InvoicedUntil" />
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="form-group">
                            <label>&nbsp;</label>
                            <div>
                                <button type="button" class="btn btn-default btn-sm" @click="applyRevertDate">
                                    Revert to invoice start date ({{ getRevertInvoicedUntilDate() }})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Order rows -->
                <hr />
                <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px">
                    <h4 style="margin: 0">Order rows</h4>
                    <button type="button" class="btn btn-default btn-sm" @click="addRow">
                        <span class="glyphicon glyphicon-plus"></span>
                        Add row
                    </button>
                </div>

                <!-- Column headers -->
                <div v-if="state.form.OrderRows.length > 0" class="row" style="font-weight: 600; font-size: 12px; color: #777; margin-bottom: 4px; padding: 0 4px">
                    <div class="col-md-4">Specification</div>
                    <div class="col-md-2">Amount</div>
                    <div class="col-md-2">Bookkeeping</div>
                    <div class="col-md-2">Refers to month</div>
                    <div class="col-md-2"></div>
                </div>

                <div
                    v-for="(row, index) in state.form.OrderRows"
                    :key="index"
                    class="row"
                    style="align-items: center; background: #fafafa; border: 1px solid #e8e8e8; border-radius: 4px; margin: 0 0 6px 0; padding: 6px 0">
                    <div class="col-md-4">
                        <input type="text" class="form-control" v-model="row.Specification" placeholder="Description" />
                    </div>
                    <div class="col-md-2">
                        <input type="number" step="0.01" class="form-control text-right" v-model.number="row.Amount" />
                    </div>
                    <div class="col-md-2">
                        <select class="form-control" v-model="row.BookkeepingAccount">
                            <option v-for="acc in props.bookkeepingAccounts" :key="acc" :value="acc">{{ acc }}</option>
                        </select>
                    </div>
                    <div class="col-md-2">
                        <MonthPicker v-model="row.RefersToMonth" />
                    </div>
                    <div class="col-md-2" style="white-space: nowrap">
                        <div class="btn-group btn-group-sm">
                            <button type="button" class="btn btn-default" title="Move up" :disabled="index === 0" @click="moveRowUp(index)">
                                <span class="glyphicon glyphicon-arrow-up"></span>
                            </button>
                            <button
                                type="button"
                                class="btn btn-default"
                                title="Move down"
                                :disabled="index === state.form.OrderRows.length - 1"
                                @click="moveRowDown(index)">
                                <span class="glyphicon glyphicon-arrow-down"></span>
                            </button>
                            <button type="button" class="btn btn-default btn-danger" title="Delete row" @click="removeRow(index)">
                                <span class="glyphicon glyphicon-trash"></span>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="state.form.OrderRows.length > 0" style="text-align: right; padding: 4px 8px; font-weight: 600">
                    Total: {{ state.form.OrderRows.reduce((s, r) => s + r.Amount, 0).toFixed(2) }} {{ state.form.CurrencyCode }}
                </div>

                <div v-if="state.form.OrderRows.length === 0" class="text-muted m-sm-b">No rows added yet.</div>
            </form>
        </template>

        <template #button>
            <button type="button" class="btn btn-primary" :disabled="!canSave || state.saving" @click="onSave">
                <span v-if="state.saving">Saving...</span>
                <span v-else>Save order</span>
            </button>
        </template>
    </Modal>
</template>

<script lang="ts">
    export default { name: 'BillingOrderEditor' };
</script>

<style></style>
